import { CommonModule } from '@angular/common';

import { TPipe } from '../../../pipe/t.pipe';
import { LocaleService } from '../../../services/locale.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  booleanAttribute,
  forwardRef,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface PrettySelectOption<T = string | number> {
  value: T;
  label: string;
  /** Optional secondary text on the right side of the option. */
  hint?: string;
  /** Optional SVG markup or inline content shown before the label. */
  icon?: string;
  /** Disable this specific option. */
  disabled?: boolean;
}

/**
 * Custom select with a styled dropdown panel. Compatible with Reactive
 * Forms (`[formControl]`, `formControlName`) and template-driven forms
 * (`[(ngModel)]`).
 *
 *   <app-pretty-select
 *     [formControl]="statusCtrl"
 *     [options]="statusOptions"
 *     placeholder="Tous statuts"
 *     searchable
 *     clearable />
 *
 * The trigger and the panel are both fully Tailwind-styled (no native
 * `<select>`) so the visual matches the rest of the design system.
 */
@Component({
  selector: 'app-pretty-select',
  imports: [CommonModule, FormsModule, TPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrettySelectComponent),
      multi: true,
    },
  ],
  template: `
    <div class="relative">
      <button
        type="button"
        (click)="toggle()"
        [disabled]="disabled"
        [attr.aria-expanded]="isOpen"
        [attr.aria-haspopup]="'listbox'"
        class="flex h-11 w-full items-center justify-between gap-2 rounded-lg border bg-transparent px-4 py-2.5 text-left text-sm shadow-theme-xs focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-900"
        [class.border-brand-300]="isOpen"
        [class.ring-3]="isOpen"
        [class.ring-brand-500/20]="isOpen"
        [class.dark:border-brand-800]="isOpen"
        [class.border-gray-300]="!isOpen"
        [class.dark:border-gray-700]="!isOpen"
        [class.hover:border-gray-400]="!isOpen && !disabled"
        [class.dark:hover:border-gray-600]="!isOpen && !disabled"
      >
        <span
          class="truncate"
          [class.text-gray-800]="!!selectedLabel"
          [class.dark:text-white/90]="!!selectedLabel"
          [class.text-gray-400]="!selectedLabel"
          [class.dark:text-gray-500]="!selectedLabel"
        >
          {{ selectedLabel || placeholder }}
        </span>
        <span class="flex flex-shrink-0 items-center gap-1.5">
          @if (clearable && hasValue && !disabled) {
            <button
              type="button"
              (click)="clear($event)"
              class="rounded p-0.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/5"
              [attr.aria-label]="'Effacer'"
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                <path d="M14.83 5.17a1 1 0 0 0-1.41 0L10 8.59 6.59 5.17a1 1 0 1 0-1.42 1.42L8.59 10l-3.42 3.41a1 1 0 1 0 1.42 1.42L10 11.41l3.41 3.42a1 1 0 0 0 1.42-1.42L11.41 10l3.42-3.41a1 1 0 0 0 0-1.42Z" />
              </svg>
            </button>
          }
          <svg
            class="text-gray-400 transition-transform duration-200"
            [class.rotate-180]="isOpen"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5.516 7.548 10 12.032l4.484-4.484a.75.75 0 0 1 1.06 1.061l-5.014 5.014a.75.75 0 0 1-1.06 0L4.456 8.609a.75.75 0 0 1 1.06-1.06Z" />
          </svg>
        </span>
      </button>

      @if (isOpen) {
        <div
          role="listbox"
          class="absolute z-30 mt-1.5 w-full rounded-xl border border-gray-200 bg-white p-1 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
        >
          @if (searchable) {
            <div class="p-1">
              <input
                #searchInput
                type="text"
                [(ngModel)]="filter"
                (click)="$event.stopPropagation()"
                (keydown.escape)="close()"
                placeholder="Filtrer…"
                class="h-9 w-full rounded-lg border border-gray-200 bg-transparent px-3 text-sm focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              />
            </div>
          }
          <ul class="max-h-64 overflow-y-auto py-1">
            @if (filteredOptions.length === 0) {
              <li class="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                Aucun résultat.
              </li>
            }
            @for (opt of filteredOptions; track opt.value) {
              <li>
                <button
                  type="button"
                  (click)="select(opt)"
                  [disabled]="opt.disabled"
                  role="option"
                  [attr.aria-selected]="isSelected(opt)"
                  class="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50"
                  [class.bg-brand-50]="isSelected(opt)"
                  [class.text-brand-700]="isSelected(opt)"
                  [class.dark:bg-brand-500/10]="isSelected(opt)"
                  [class.dark:text-brand-400]="isSelected(opt)"
                  [class.text-gray-700]="!isSelected(opt)"
                  [class.dark:text-gray-300]="!isSelected(opt)"
                  [class.hover:bg-gray-50]="!isSelected(opt) && !opt.disabled"
                  [class.dark:hover:bg-white/5]="!isSelected(opt) && !opt.disabled"
                >
                  <span class="flex min-w-0 items-center gap-2">
                    @if (opt.icon) {
                      <span class="flex-shrink-0" [innerHTML]="opt.icon"></span>
                    }
                    <span class="truncate">{{ opt.label | t }}</span>
                    @if (opt.hint) {
                      <span class="truncate text-xs text-gray-400 dark:text-gray-500">{{ opt.hint }}</span>
                    }
                  </span>
                  @if (isSelected(opt)) {
                    <svg class="flex-shrink-0 text-brand-500" width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0Z" />
                    </svg>
                  }
                </button>
              </li>
            }
          </ul>
        </div>
      }
    </div>
  `,
})
export class PrettySelectComponent<T = string | number> implements ControlValueAccessor {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  /** i18n : `option.label` peut être une **clé** ou du texte brut (repli `t()`). */
  private readonly locale = inject(LocaleService);

  @Input() options: PrettySelectOption<T>[] = [];
  @Input() placeholder = 'Sélectionner…';
  @Input({ transform: booleanAttribute }) searchable = false;
  @Input({ transform: booleanAttribute }) clearable = false;
  /** Optional custom equality function (default: ===). Useful for object values. */
  @Input() compareWith: (a: T | null, b: T | null) => boolean = (a, b) => a === b;

  value: T | null = null;
  disabled = false;
  isOpen = false;
  filter = '';

  // ControlValueAccessor wiring
  private onChange: (v: T | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(v: T | null): void {
    this.value = v;
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: T | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void {
    this.disabled = d;
    this.cdr.markForCheck();
  }

  // ── interaction ────────────────────────────────────────────────────────
  toggle(): void {
    if (this.disabled) return;
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    this.filter = '';
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.onTouched();
    this.cdr.markForCheck();
  }

  select(opt: PrettySelectOption<T>): void {
    if (opt.disabled) return;
    this.value = opt.value;
    this.onChange(opt.value);
    this.close();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.value = null;
    this.onChange(null);
    this.cdr.markForCheck();
  }

  isSelected(opt: PrettySelectOption<T>): boolean {
    return this.compareWith(opt.value, this.value);
  }

  // ── derived ────────────────────────────────────────────────────────────
  get selectedLabel(): string | null {
    if (this.value === null || this.value === undefined) return null;
    const opt = this.options.find((o) => this.compareWith(o.value, this.value));
    return opt ? this.locale.t(opt.label) : null;
  }

  get hasValue(): boolean {
    // For string-typed selects, treat empty string as "no value" so the
    // clear button doesn't show for a meaningless reset.
    return this.value !== null && this.value !== undefined && (this.value as unknown) !== '';
  }

  get filteredOptions(): PrettySelectOption<T>[] {
    if (!this.searchable || !this.filter) return this.options;
    const q = this.filter.toLowerCase().trim();
    if (!q) return this.options;
    // Filtre sur le libellé **traduit** : l'utilisateur tape ce qu'il voit.
    return this.options.filter(
      (o) =>
        this.locale.t(o.label).toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q) ||
        (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }

  // ── close on outside click / Escape ────────────────────────────────────
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.el.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }
}
